/**
 * ActionDropdown Component
 *
 * The detail-modal header "⋯" menu for secondary actions. Mirrors the
 * right-click row menu: actions are grouped by category (Communicate, Create,
 * Update status, …) under non-interactive section labels, the low-priority
 * integration/admin tail folds behind a "More actions" submenu, and the
 * destructive zone is pinned last. See lib/actions/action-categories.ts.
 */

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreHorizontal } from "lucide-react";
import {
  MORE_ACTIONS_LABEL,
  partitionActionGroups,
  type ActionCategory,
} from "@/lib/actions/action-categories";
import type { DetailAction, BaseEntity } from "../types/detailPage";

interface ActionDropdownProps<T extends BaseEntity> {
  actions: DetailAction<T>[];
  loadingActions: Set<string>;
  item: T;
  onActionClick: (action: DetailAction<T>) => void;
}

const ActionDropdownBase: React.FC<ActionDropdownProps<any>> = ({
  actions,
  loadingActions,
  item,
  onActionClick,
}) => {
  const { top, more, danger } = React.useMemo(
    () => partitionActionGroups(actions),
    [actions]
  );

  const renderItem = useCallback(
    (action: DetailAction<any>) => {
      const label =
        typeof action.label === "function" ? action.label(item) : action.label;
      const isDisabled =
        action.disabled &&
        typeof action.disabled === "function" &&
        action.disabled(item);
      return (
        <DropdownMenuItem
          key={action.id}
          onSelect={() => onActionClick(action)}
          disabled={isDisabled}
          variant={action.variant === "destructive" ? "destructive" : "default"}
          className="cursor-pointer"
        >
          <div className="flex items-center gap-2 w-full">
            {loadingActions.has(action.id) && (
              <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
            )}
            <span className="truncate">{label}</span>
          </div>
        </DropdownMenuItem>
      );
    },
    [item, loadingActions, onActionClick]
  );

  // Non-interactive category header (styled label, NOT a selectable item) so
  // categories read as distinct groups. The list stays short via the partition
  // — the integration/admin tail folds behind "More actions".
  const renderGroup = useCallback(
    (group: { category: ActionCategory; label: string; items: DetailAction<any>[] }) => (
      <React.Fragment key={group.category}>
        <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.label}
        </DropdownMenuLabel>
        {group.items.map(renderItem)}
      </React.Fragment>
    ),
    [renderItem]
  );

  if (actions.length === 0) {
    return null;
  }

  const needsSeparatorBeforeMore = top.length > 0;
  const hasDanger = danger.flatMap((g) => g.items).length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="default"
          className="min-h-[44px] min-w-[44px] touch-manipulation"
          title="More options"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      {/*
        z-index-allow: this menu is rendered both on the plain detail page
        AND inside the Mantine-backed entity detail modal (ResponsiveModal,
        which renders at z-index: 200 — see UniversalDetailModalLayout.tsx /
        DetailModalContent.tsx and docs/engineering-rules/Z_INDEX_AND_OVERFLOW.md
        "Popovers inside Mantine modals"). The whole shared token scale tops
        out at z-drawer (80), well below 200, so a normal named token would
        render this menu BEHIND that modal. z-max (9999) is the sanctioned
        escape hatch for exactly this case. `collisionPadding` keeps every
        item inside the viewport instead of flush against its edge — Radix's
        own collision avoidance already repositions on overflow, this just
        adds a safety margin so nothing is ever clipped by an edge.
      */}
      <DropdownMenuContent
        align="start"
        className="w-56 max-h-[400px] overflow-y-auto z-max"
        sideOffset={8}
        collisionPadding={8}
      >
        {top.map((group, groupIndex) => (
          <React.Fragment key={group.category}>
            {groupIndex > 0 && <DropdownMenuSeparator />}
            {renderGroup(group)}
          </React.Fragment>
        ))}

        {more.length > 0 && (
          <>
            {needsSeparatorBeforeMore && <DropdownMenuSeparator />}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>{MORE_ACTIONS_LABEL}</DropdownMenuSubTrigger>
              {/*
                z-index-allow: same reason as the parent DropdownMenuContent
                above. Without this the submenu falls back to the shadcn
                default (z-50, baked into DropdownMenuSubContent's own base
                className) and — even though the parent menu is correctly on
                top of the Mantine modal — the "More actions" flyout renders
                BEHIND it, which reads as "the menu opened in the wrong
                place" when really the item it opened was just invisible.
              */}
              <DropdownMenuSubContent className="w-56 max-h-[400px] overflow-y-auto z-max">
                {more.map((group, groupIndex) => (
                  <React.Fragment key={group.category}>
                    {groupIndex > 0 && <DropdownMenuSeparator />}
                    {renderGroup(group)}
                  </React.Fragment>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}

        {hasDanger && (
          <>
            {(top.length > 0 || more.length > 0) && <DropdownMenuSeparator />}
            {danger.map((group) => group.items.map(renderItem))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ActionDropdown = React.memo(ActionDropdownBase);
